import { useMemo } from 'react';

import { type Tool } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { NuggetChip } from '@backrs_ui/grow-ui/src/components/Nuggets/NuggetChip';
import { isStructuredSession } from '@backrs_ui/grow-ui/src/models/models';
import { PublicProfileSection } from '@backrs_ui/grow-ui/src/pages/Profile/atoms/PublicProfileSection';
import {
  setMemberSolutionSession,
  type SelectedSolutionSession,
} from '@backrs_ui/grow-ui/src/redux/coachingDashboard/memberSolutionSession';
import { useGetSessionStatusesQuery } from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.api';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { openCustomTab } from '@backrs_ui/grow-ui/src/redux/tabs/tabs.slice';
import { useGetToolsQuery } from '@backrs_ui/grow-ui/src/redux/tools/tools.api';

import { Box, type SxProps, type Theme, Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

import { useAgentContext } from './SolutionSessionsSection.agentContext';

const {
  modules: { coachingDashboard },
} = appStrings;

export type CompletedSolutionSession = SelectedSolutionSession & Readonly<{ tool: Tool }>;

type SolutionSessionsSectionProps = Readonly<{
  memberId: number;
}>;

export const SolutionSessionsSection = ({ memberId }: SolutionSessionsSectionProps) => {
  const dispatch = useAppDispatch();

  const { data, isError } = useGetSessionStatusesQuery(
    memberId ? { coaching_user_id: memberId } : skipToken,
  );
  const { data: tools, isError: isToolsError } = useGetToolsQuery(undefined);

  const completedSessions: CompletedSolutionSession[] = useMemo(() => {
    if (!data || !tools) return [];

    return Object.entries(data.statuses).flatMap(([sessionType, { status, session_id }]) => {
      if (status !== 'complete') return [];

      // a session the student ran inside a chat has no id a coach can open
      if (session_id === null) {
        console.warn('Completed solution session has no openable output', sessionType);

        return [];
      }

      if (!isStructuredSession(sessionType)) {
        console.warn('Unknown solution session type; dropping its chip', sessionType);

        return [];
      }

      const tool = tools.find((candidate) => candidate.structured_session_type === sessionType);

      if (!tool) {
        console.warn('No tool for completed solution session; dropping its chip', sessionType);

        return [];
      }

      return [{ sessionType, sessionId: session_id, title: tool.title, tool }];
    });
  }, [data, tools]);

  useAgentContext(completedSessions);

  const handleSessionClick = ({ sessionType, sessionId, title }: CompletedSolutionSession) => {
    dispatch(setMemberSolutionSession({ sessionType, sessionId, title }));
    // openCustomTab both opens and selects the tab.
    dispatch(openCustomTab('memberSolutionSession'));
  };

  if (isError || isToolsError) {
    return (
      <PublicProfileSection
        title={coachingDashboard.solutionSessionsTitle}
        variant={{
          type: 'CHILDREN',
          children: (
            <Typography variant="text-sm-regular" sx={errorTextSx}>
              {coachingDashboard.failedToLoadSolutionSessions}
            </Typography>
          ),
        }}
      />
    );
  }

  if (completedSessions.length === 0) return null;

  return (
    <PublicProfileSection
      title={coachingDashboard.solutionSessionsTitle}
      variant={{
        type: 'CHILDREN',
        children: (
          <Box sx={chipGroupSx}>
            {completedSessions.map((session) => (
              <SolutionSessionChip
                key={session.sessionType}
                session={session}
                onClick={handleSessionClick}
              />
            ))}
          </Box>
        ),
      }}
    />
  );
};

type SolutionSessionChipProps = Readonly<{
  session: CompletedSolutionSession;
  onClick: (session: CompletedSolutionSession) => void;
}>;

const SolutionSessionChip = ({ session, onClick }: SolutionSessionChipProps) => {
  const imageUrl = session.tool.banner_media?.media_url ?? session.tool.media?.media_url;

  return (
    <NuggetChip
      id={`member-solution-session-${session.sessionType}`}
      label={session.title}
      image={imageUrl ? { src: imageUrl, alt: session.title } : undefined}
      onClick={() => onClick(session)}
      sx={chipSx}
    />
  );
};

const chipGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-sm'],
});

const chipSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Base/Black'],
});

const errorTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Error/600'],
});
